import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

// ===== ASSIGN MITRA MODAL COMPONENT (Inline) =====
const AssignMitraModal = ({ booking, onSuccess, onClose }) => {
    const [availableMitras, setAvailableMitras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [selectedMitra, setSelectedMitra] = useState(null);
    
    const API_BASE_URL = 'https://api.tomassage.id/api';
    
    useEffect(() => {
        if (booking?.id) {
            fetchAvailableMitras();
        }
    }, [booking]);
    
    const fetchAvailableMitras = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await axios.get(
                `${API_BASE_URL}/bookings/${booking.id}/available-mitras`
            );
            
            setAvailableMitras(response.data.data || []);
            
            if (response.data.data?.length === 0) {
                setError('Tidak ada mitra yang tersedia untuk booking ini');
            }
            
        } catch (err) {
            console.error('Error fetching available mitras:', err);
            setError(err.response?.data?.message || 'Gagal memuat daftar mitra');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAssignMitra = async (mitraId) => {
        try {
            setAssigning(true);
            setError(null);
            
            const response = await axios.post(
                `${API_BASE_URL}/bookings/${booking.id}/assign-mitra`,
                { mitraId }
            );
            
            if (response.data.success) {
                if (onSuccess) {
                    onSuccess(response.data.data);
                }
                if (onClose) {
                    onClose();
                }
            }
            
        } catch (err) {
            console.error('Error assigning mitra:', err);
            setError(err.response?.data?.message || 'Gagal menugaskan mitra');
        } finally {
            setAssigning(false);
        }
    };
    
    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(price);
    };
    
    if (!booking) return null;
    
    return (
        <div className="modal-content">
            <div className="modal-header bg-primary text-white">
                <h5 className="modal-title d-flex align-items-center">
                    <Icon icon="mdi:account-plus" className="me-2" />
                    Tugaskan Mitra - Booking #{booking.id}
                </h5>
                <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={onClose}
                    disabled={assigning}
                ></button>
            </div>
            
            <div className="modal-body">
                {/* Booking Info */}
                <div className="card bg-light mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <h6 className="fw-bold">{booking.nama_service}</h6>
                                <p className="text-muted small mb-1">{booking.varian_service}</p>
                                <p className="mb-0">
                                    <Icon icon="mdi:map-marker" className="text-danger" />
                                    <small className="ms-1">{booking.alamat_user}</small>
                                </p>
                            </div>
                            <div className="col-md-6 text-md-end">
                                <h5 className="text-success mb-1">{formatPrice(booking.harga_service)}</h5>
                                <p className="text-muted small mb-0">
                                    <Icon icon="mdi:calendar" className="me-1" />
                                    {booking.tanggal_booking}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Error Message */}
                {error && (
                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                        <Icon icon="mdi:alert-circle" className="me-2" />
                        {error}
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setError(null)}
                        ></button>
                    </div>
                )}
                
                {/* Loading State */}
                {loading && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Mencari mitra yang tersedia...</p>
                    </div>
                )}
                
                {/* Mitras List */}
                {!loading && availableMitras.length > 0 && (
                    <>
                        <h6 className="mb-3">
                            <Icon icon="mdi:account-group" className="me-2 text-primary" />
                            Mitra Tersedia ({availableMitras.length})
                        </h6>
                        
                        <div className="list-group">
                            {availableMitras.map((mitra) => (
                                <div 
                                    key={mitra.id}
                                    className={`list-group-item list-group-item-action ${
                                        selectedMitra?.id === mitra.id ? 'active' : ''
                                    }`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedMitra(mitra)}
                                >
                                    <div className="d-flex w-100 justify-content-between align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <Icon 
                                                    icon="mdi:account-circle" 
                                                    className="me-2" 
                                                    style={{ fontSize: '2rem' }}
                                                />
                                                <div>
                                                    <h6 className="mb-0">{mitra.nama_lengkap}</h6>
                                                    <small className={selectedMitra?.id === mitra.id ? 'text-white-50' : 'text-muted'}>
                                                        <Icon icon="mdi:gender-male-female" className="me-1" />
                                                        {mitra.jenis_kelamin}
                                                        <span className="mx-2">|</span>
                                                        <Icon icon="mdi:phone" className="me-1" />
                                                        {mitra.nomor_wa}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 mt-2">
                                                <span className={`badge ${
                                                    selectedMitra?.id === mitra.id 
                                                        ? 'bg-white text-primary' 
                                                        : 'bg-info'
                                                }`}>
                                                    <Icon icon="mdi:map-marker-distance" className="me-1" />
                                                    {mitra.distance} km
                                                </span>
                                                <span className={`badge ${
                                                    selectedMitra?.id === mitra.id 
                                                        ? 'bg-white text-success' 
                                                        : 'bg-success'
                                                }`}>
                                                    <Icon icon="mdi:wallet" className="me-1" />
                                                    Saldo: {formatPrice(mitra.balance)}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {selectedMitra?.id === mitra.id && (
                                            <Icon 
                                                icon="mdi:check-circle" 
                                                className="text-white" 
                                                style={{ fontSize: '1.5rem' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                
                {/* Empty State */}
                {!loading && availableMitras.length === 0 && (
                    <div className="text-center py-5">
                        <Icon 
                            icon="mdi:account-off" 
                            style={{ fontSize: '4rem' }} 
                            className="text-muted mb-3" 
                        />
                        <h5>Tidak Ada Mitra Tersedia</h5>
                        <p className="text-muted">
                            Tidak ada mitra yang memenuhi kriteria untuk booking ini.
                            <br />
                            Silakan coba lagi nanti atau hubungi customer untuk reschedule.
                        </p>
                        <button 
                            className="btn btn-outline-primary mt-2"
                            onClick={fetchAvailableMitras}
                        >
                            <Icon icon="mdi:refresh" className="me-1" />
                            Coba Lagi
                        </button>
                    </div>
                )}
            </div>
            
            <div className="modal-footer">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={assigning}
                >
                    <Icon icon="mdi:close" className="me-1" />
                    Batal
                </button>
                
                {selectedMitra && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleAssignMitra(selectedMitra.id)}
                        disabled={assigning || !selectedMitra}
                    >
                        {assigning ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Menugaskan...
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:account-check" className="me-1" />
                                Tugaskan {selectedMitra.nama_lengkap}
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

// ===== MAIN KANBAN LAYER COMPONENT =====
const KanbanLayer = () => {
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState({});
    const [mitras, setMitras] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    
    // Current booking being viewed
    const [currentBooking, setCurrentBooking] = useState(null);
    
    // Assign Mitra Modal State
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [bookingToAssign, setBookingToAssign] = useState(null);
    
    // Filter & search state
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState('all');
    
    // Date range filter state
    const [dateRangeType, setDateRangeType] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // API config
    const API_BASE_URL = 'https://api.tomassage.id/api';
    
    // Fetch bookings data
    const fetchBookings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/bookings`);
            setBookings(response.data.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching bookings:', err);
            setError('Gagal memuat data transaksi: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch users data
    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/users`);
            const usersMap = {};
            response.data.data.forEach(user => {
                usersMap[user.id] = user;
            });
            setUsers(usersMap);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };
    
    // Fetch mitras data
    const fetchMitras = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/mitra`);
            const mitrasMap = {};
            response.data.data.forEach(mitra => {
                mitrasMap[mitra.id] = mitra;
            });
            setMitras(mitrasMap);
        } catch (err) {
            console.error('Error fetching mitras:', err);
        }
    };
    
    // Initial fetch
    useEffect(() => {
        const fetchAllData = async () => {
            await fetchBookings();
            await fetchUsers();
            await fetchMitras();
        };
        fetchAllData();
    }, []);
    
    // Format price
    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };
    
    // Parse Indonesian date format
    const parseIndonesianDate = (dateString) => {
        if (!dateString) return null;
        
        const months = {
            'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3,
            'Mei': 4, 'Juni': 5, 'Juli': 6, 'Agustus': 7,
            'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
        };
        
        try {
            const parts = dateString.trim().split(' ');
            if (parts.length === 3) {
                const day = parseInt(parts[0]);
                const month = months[parts[1]];
                const year = parseInt(parts[2]);
                
                if (day && month !== undefined && year) {
                    return new Date(year, month, day);
                }
            }
            return new Date(dateString);
        } catch (e) {
            console.warn('Failed to parse date:', dateString);
            return null;
        }
    };

    // Format date to Indonesian format
    const formatDate = (dateString) => {
        try {
            const date = parseIndonesianDate(dateString) || new Date(dateString);
            return date.toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format date for comparison
    const formatDateForComparison = (dateString) => {
        const date = parseIndonesianDate(dateString);
        if (!date || isNaN(date.getTime())) return null;
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    };

    // Get date range based on type
    const getDateRange = () => {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        switch (dateRangeType) {
            case 'today':
                return { start: todayStr, end: todayStr };
            
            case 'week':
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                return {
                    start: weekStart.toISOString().split('T')[0],
                    end: weekEnd.toISOString().split('T')[0]
                };
            
            case 'month':
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return {
                    start: monthStart.toISOString().split('T')[0],
                    end: monthEnd.toISOString().split('T')[0]
                };
            
            case 'custom':
                return { start: customStartDate, end: customEndDate };
            
            default:
                return null;
        }
    };

    // Check if date is in range
    const isDateInRange = (bookingDate, dateRange) => {
        if (!dateRange || !bookingDate) return true;
        
        const bookingDateFormatted = formatDateForComparison(bookingDate);
        if (!bookingDateFormatted) return true;
        
        const startDate = dateRange.start;
        const endDate = dateRange.end;
        
        return bookingDateFormatted >= startDate && bookingDateFormatted <= endDate;
    };
    
    // Update booking status
    const updateBookingStatus = async (id, newStatus) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/bookings/${id}/status`, {
                status: newStatus,
                progress_tracking: "Diubah oleh Admin"
            });
            
            if (response.data.success || response.status === 200) {
                await fetchBookings();
                setSuccessMessage(`Status transaksi berhasil diubah menjadi ${newStatus}`);
                setTimeout(() => setSuccessMessage(null), 5000);
            }
        } catch (err) {
            console.error('Error updating booking status:', err);
            setError('Gagal mengubah status transaksi: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle assign success
    const handleAssignSuccess = (updatedBooking) => {
        setSuccessMessage('Mitra berhasil ditugaskan!');
        setShowAssignModal(false);
        setBookingToAssign(null);
        fetchBookings();
        setTimeout(() => setSuccessMessage(null), 5000);
    };
    
    // Open assign modal
    const openAssignModal = (booking) => {
        setBookingToAssign(booking);
        setShowAssignModal(true);
    };
    
    // Handle unassign mitra
    const handleUnassignMitra = async (bookingId) => {
        if (!window.confirm('Apakah Anda yakin ingin membatalkan penugasan mitra?')) {
            return;
        }
        
        try {
            const response = await axios.post(
                `${API_BASE_URL}/bookings/${bookingId}/unassign-mitra`
            );
            
            if (response.data.success) {
                setSuccessMessage('Penugasan mitra berhasil dibatalkan');
                await fetchBookings();
                setTimeout(() => setSuccessMessage(null), 5000);
            }
        } catch (err) {
            console.error('Error unassigning mitra:', err);
            setError('Gagal membatalkan penugasan mitra: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'bg-success';
            case 'pending':
                return 'bg-warning';
            case 'cancelled':
                return 'bg-danger';
            case 'on progress':
                return 'bg-info';
            default:
                return 'bg-secondary';
        }
    };

    // Reset all filters
    const resetAllFilters = () => {
        setStatusFilter('all');
        setSearchTerm('');
        setDateFilter('');
        setAssignmentFilter('all');
        setDateRangeType('all');
        setCustomStartDate('');
        setCustomEndDate('');
        setCurrentPage(1);
    };
    
    // Filter bookings
    const filteredBookings = bookings.filter(booking => {
        const matchesStatus = statusFilter === 'all' || booking.status.toLowerCase() === statusFilter.toLowerCase();
        
        const matchesSearch = 
            (booking.nama_service?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (users[booking.id_user]?.nama_lengkap?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (mitras[booking.id_mitra]?.nama_lengkap?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.alamat_user?.toLowerCase().includes(searchTerm.toLowerCase()));
        
        // Assignment filter
        const matchesAssignment = 
            assignmentFilter === 'all' ||
            (assignmentFilter === 'assigned' && booking.id_mitra) ||
            (assignmentFilter === 'unassigned' && !booking.id_mitra);
            
        // Month filter
        let matchesMonthFilter = true;
        if (dateFilter) {
            const bookingDateFormatted = formatDateForComparison(booking.tanggal_booking);
            if (bookingDateFormatted) {
                const bookingMonth = bookingDateFormatted.substring(0, 7);
                matchesMonthFilter = bookingMonth === dateFilter;
            }
        }
        
        // Date range filter
        const dateRange = getDateRange();
        const matchesDateRange = dateRangeType === 'all' || isDateInRange(booking.tanggal_booking, dateRange);
        
        return matchesStatus && matchesSearch && matchesAssignment && matchesMonthFilter && matchesDateRange;
    });
    
    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
    
    const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
    
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    
    // Get user name
    const getUserName = (userId) => {
        return users[userId]?.nama_lengkap || `User ID ${userId}`;
    };
    
    // Get mitra name
    const getMitraName = (mitraId) => {
        return mitras[mitraId]?.nama_lengkap || `Mitra ID ${mitraId}`;
    };

    // Get date range display text
    const getDateRangeDisplayText = () => {
        const dateRange = getDateRange();
        
        switch (dateRangeType) {
            case 'today':
                return `Hari Ini (${formatDate(new Date().toISOString())})`;
            case 'week':
                if (dateRange) {
                    const startDate = new Date(dateRange.start);
                    const endDate = new Date(dateRange.end);
                    return `Minggu Ini (${startDate.getDate()} - ${endDate.getDate()} ${endDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })})`;
                }
                return 'Minggu Ini';
            case 'month':
                if (dateRange) {
                    const monthDate = new Date(dateRange.start);
                    return `Bulan ${monthDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}`;
                }
                return 'Bulan Ini';
            case 'custom':
                if (customStartDate && customEndDate) {
                    const start = new Date(customStartDate);
                    const end = new Date(customEndDate);
                    
                    if (customStartDate === customEndDate) {
                        return `${start.getDate()} ${start.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}`;
                    } else {
                        return `${start.getDate()} ${start.toLocaleDateString('id-ID', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' })}`;
                    }
                }
                return 'Rentang Kustom';
            default:
                return 'Semua Tanggal';
        }
    };
    
    // Render loading state
    if (loading && bookings.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="row gy-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
                            <h4 className="mb-0 d-flex align-items-center">
                                <Icon icon="mdi:calendar-check" className="me-2" />
                                Manajemen Booking & Transaksi
                            </h4>
                            <button
                                className="btn btn-light d-flex align-items-center gap-2"
                                onClick={() => fetchBookings()}
                            >
                                <Icon icon="mdi:refresh" />
                                Refresh Data
                            </button>
                        </div>
                        
                        <div className="card-body p-4">
                            {/* Success Message */}
                            {successMessage && (
                                <div className="alert alert-success alert-dismissible fade show" role="alert">
                                    <Icon icon="mdi:check-circle" className="me-2" />
                                    {successMessage}
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setSuccessMessage(null)}
                                    ></button>
                                </div>
                            )}
                            
                            {/* Error Message */}
                            {error && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    <Icon icon="mdi:alert-circle" className="me-2" />
                                    {error}
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setError(null)}
                                    ></button>
                                </div>
                            )}
                            
                            {/* Search and Filter */}
                            <div className="row mb-4">
                                <div className="col-md-3 mb-3 mb-md-0">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <Icon icon="mdi:magnify" />
                                        </span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Cari transaksi..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-9 d-flex gap-2 flex-wrap">
                                    <select 
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">Semua Status</option>
                                        <option value="pending">Pending</option>
                                        <option value="on progress">On Progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    
                                    <select 
                                        className="form-select"
                                        value={assignmentFilter}
                                        onChange={(e) => {
                                            setAssignmentFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">Semua Assignment</option>
                                        <option value="assigned">Sudah Ditugaskan</option>
                                        <option value="unassigned">Belum Ditugaskan</option>
                                    </select>
                                    
                                    <input 
                                        type="month" 
                                        className="form-control" 
                                        value={dateFilter}
                                        onChange={(e) => {
                                            setDateFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Filter bulan"
                                        title="Filter berdasarkan bulan"
                                    />
                                    
                                    <button 
                                        className="btn btn-outline-secondary" 
                                        onClick={resetAllFilters}
                                        title="Reset Semua Filter"
                                    >
                                        <Icon icon="mdi:filter-remove" />
                                    </button>
                                </div>
                            </div>

                            {/* Date Range Filter */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div className="card border-0 bg-light">
                                        <div className="card-header bg-white border-bottom py-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0 d-flex align-items-center">
                                                    <Icon icon="mdi:calendar-range" className="me-2 text-primary" />
                                                    Filter Rentang Tanggal
                                                </h6>
                                                <span className="badge bg-primary fs-6">{getDateRangeDisplayText()}</span>
                                            </div>
                                        </div>
                                        <div className="card-body py-3">
                                            <div className="row align-items-end g-3">
                                                <div className="col-lg-3 col-md-6">
                                                    <label className="form-label small fw-semibold">
                                                        <Icon icon="mdi:calendar-clock" className="me-1" />
                                                        Rentang Waktu
                                                    </label>
                                                    <select 
                                                        className="form-select"
                                                        value={dateRangeType}
                                                        onChange={(e) => {
                                                            setDateRangeType(e.target.value);
                                                            setCurrentPage(1);
                                                        }}
                                                    >
                                                        <option value="all">🗓️ Semua Tanggal</option>
                                                        <option value="today">📅 Hari Ini</option>
                                                        <option value="week">📊 Minggu Ini</option>
                                                        <option value="month">📈 Bulan Ini</option>
                                                        <option value="custom">⚙️ Rentang Kustom</option>
                                                    </select>
                                                </div>
                                                
                                                {dateRangeType === 'custom' && (
                                                    <>
                                                        <div className="col-lg-3 col-md-6">
                                                            <label className="form-label small fw-semibold">
                                                                <Icon icon="mdi:calendar-start" className="me-1" />
                                                                Tanggal Mulai
                                                            </label>
                                                            <input 
                                                                type="date" 
                                                                className="form-control"
                                                                value={customStartDate}
                                                                onChange={(e) => {
                                                                    setCustomStartDate(e.target.value);
                                                                    setCurrentPage(1);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-lg-3 col-md-6">
                                                            <label className="form-label small fw-semibold">
                                                                <Icon icon="mdi:calendar-end" className="me-1" />
                                                                Tanggal Selesai
                                                            </label>
                                                            <input 
                                                                type="date" 
                                                                className="form-control"
                                                                value={customEndDate}
                                                                onChange={(e) => {
                                                                    setCustomEndDate(e.target.value);
                                                                    setCurrentPage(1);
                                                                }}
                                                                min={customStartDate}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                
                                                <div className="col-lg-3 col-md-6">
                                                    <button 
                                                        className="btn btn-outline-danger w-100"
                                                        onClick={() => {
                                                            setDateRangeType('all');
                                                            setCustomStartDate('');
                                                            setCustomEndDate('');
                                                            setCurrentPage(1);
                                                        }}
                                                        title="Reset Filter Tanggal"
                                                    >
                                                        <Icon icon="mdi:calendar-remove" className="me-1" />
                                                        Reset Tanggal
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Results Summary */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="card border-0 bg-info bg-opacity-10">
                                        <div className="card-body py-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <Icon icon="mdi:information-outline" className="me-2 text-info" />
                                                    <small className="text-dark fw-medium">
                                                        {filteredBookings.length === bookings.length ? (
                                                            <>Menampilkan <span className="fw-bold text-primary">{filteredBookings.length}</span> total transaksi</>
                                                        ) : (
                                                            <>
                                                                Ditemukan <span className="fw-bold text-success">{filteredBookings.length}</span> dari <span className="fw-bold">{bookings.length}</span> transaksi
                                                            </>
                                                        )}
                                                    </small>
                                                </div>
                                                
                                                {(searchTerm || statusFilter !== 'all' || assignmentFilter !== 'all' || dateFilter || dateRangeType !== 'all') && (
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                                                        onClick={resetAllFilters}
                                                        title="Hapus Semua Filter"
                                                    >
                                                        <Icon icon="mdi:filter-remove" className="me-1" />
                                                        <span className="d-none d-sm-inline">Hapus Filter</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Bookings Table */}
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ID</th>
                                            <th>Tanggal</th>
                                            <th>Customer</th>
                                            <th>Terapis</th>
                                            <th>Layanan</th>
                                            <th>Harga</th>
                                            <th>Status</th>
                                            <th>Progress</th>
                                            <th className="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map(booking => (
                                                <tr key={booking.id}>
                                                    <td>{booking.id}</td>
                                                    <td>{booking.tanggal_booking}</td>
                                                    <td>{getUserName(booking.id_user)}</td>
                                                    <td>
                                                        {booking.id_mitra ? (
                                                            <div>
                                                                <Icon icon="mdi:account-check" className="text-success me-1" />
                                                                {getMitraName(booking.id_mitra)}
                                                            </div>
                                                        ) : (
                                                            <div className="text-muted">
                                                                <Icon icon="mdi:account-off" className="me-1" />
                                                                Belum ditugaskan
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {booking.nama_service}
                                                        <br/>
                                                        <small className="text-muted">{booking.varian_service}</small>
                                                    </td>
                                                    <td>{formatPrice(booking.harga_service)}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadgeColor(booking.status)}`}>
                                                            {booking.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small>{booking.progress_tracking}</small>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="btn-group">
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => setCurrentBooking(booking)}
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#viewBookingModal"
                                                                title="Lihat Detail"
                                                            >
                                                                <Icon icon="mdi:eye" />
                                                            </button>
                                                            
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => setCurrentBooking(booking)}
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#mapModal"
                                                                title="Lihat Lokasi"
                                                            >
                                                                <Icon icon="mdi:map-marker" />
                                                            </button>
                                                            
                                                            {/* Tombol Assign Mitra - jika Pending dan belum ada mitra */}
                                                            {booking.status === 'Pending' && !booking.id_mitra && (
                                                                <button
                                                                    className="btn btn-sm btn-warning"
                                                                    onClick={() => openAssignModal(booking)}
                                                                    title="Tugaskan Mitra"
                                                                >
                                                                    <Icon icon="mdi:account-plus" />
                                                                </button>
                                                            )}
                                                            
                                                            {/* Menu Dropdown */}
                                                            <div className="btn-group">
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                                    data-bs-toggle="dropdown"
                                                                    title="Aksi Lainnya"
                                                                >
                                                                    <Icon icon="mdi:dots-vertical" />
                                                                </button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    {/* Assign Mitra */}
                                                                    {booking.status === 'Pending' && !booking.id_mitra && (
                                                                        <>
                                                                            <li>
                                                                                <button 
                                                                                    className="dropdown-item text-warning"
                                                                                    onClick={() => openAssignModal(booking)}
                                                                                >
                                                                                    <Icon icon="mdi:account-plus" className="me-2" />
                                                                                    Tugaskan Mitra
                                                                                </button>
                                                                            </li>
                                                                            <li><hr className="dropdown-divider" /></li>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {/* Unassign Mitra */}
                                                                    {booking.id_mitra && booking.status !== 'Completed' && (
                                                                        <>
                                                                            <li>
                                                                                <button 
                                                                                    className="dropdown-item text-danger"
                                                                                    onClick={() => handleUnassignMitra(booking.id)}
                                                                                >
                                                                                    <Icon icon="mdi:account-remove" className="me-2" />
                                                                                    Batalkan Penugasan
                                                                                </button>
                                                                            </li>
                                                                            <li><hr className="dropdown-divider" /></li>
                                                                        </>
                                                                    )}
                                                                    
                                                                    <li>
                                                                        <button 
                                                                            className="dropdown-item"
                                                                            onClick={() => updateBookingStatus(booking.id, 'Completed')}
                                                                            disabled={booking.status === 'Completed'}
                                                                        >
                                                                            <Icon icon="mdi:check-circle" className="me-2 text-success" />
                                                                            Selesai
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button 
                                                                            className="dropdown-item"
                                                                            onClick={() => updateBookingStatus(booking.id, 'On Progress')}
                                                                            disabled={booking.status === 'On Progress'}
                                                                        >
                                                                            <Icon icon="mdi:progress-clock" className="me-2 text-info" />
                                                                            Dalam Proses
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button 
                                                                            className="dropdown-item"
                                                                            onClick={() => updateBookingStatus(booking.id, 'Cancelled')}
                                                                            disabled={booking.status === 'Cancelled'}
                                                                        >
                                                                            <Icon icon="mdi:cancel" className="me-2 text-danger" />
                                                                            Batalkan
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4">
                                                    <Icon icon="mdi:calendar-remove" style={{ fontSize: '3rem' }} className="text-secondary mb-2" />
                                                    <h5>Tidak ada transaksi yang ditemukan</h5>
                                                    <p className="text-muted">
                                                        {searchTerm || statusFilter !== 'all' || assignmentFilter !== 'all' || dateFilter || dateRangeType !== 'all'
                                                            ? 'Coba ubah filter pencarian Anda' 
                                                            : 'Belum ada transaksi yang dicatat'}
                                                    </p>
                                                    {(searchTerm || statusFilter !== 'all' || assignmentFilter !== 'all' || dateFilter || dateRangeType !== 'all') && (
                                                        <button 
                                                            className="btn btn-outline-primary btn-sm mt-2"
                                                            onClick={resetAllFilters}
                                                        >
                                                            <Icon icon="mdi:filter-remove" className="me-1" />
                                                            Hapus Semua Filter
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination */}
                            {filteredBookings.length > itemsPerPage && (
                                <div className="d-flex justify-content-center mt-4">
                                    <nav aria-label="Page navigation">
                                        <ul className="pagination">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    &laquo;
                                                </button>
                                            </li>
                                            {[...Array(totalPages).keys()].map(number => (
                                                <li 
                                                    key={number + 1}
                                                    className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => paginate(number + 1)}
                                                    >
                                                        {number + 1}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    &raquo;
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                            
                            {/* Counter */}
                            <div className="text-muted text-center mt-3">
                                Menampilkan {filteredBookings.length > 0 ? indexOfFirstItem + 1 : 0} - {Math.min(indexOfLastItem, filteredBookings.length)} dari {filteredBookings.length} transaksi
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* View Booking Detail Modal */}
            <div className="modal fade" id="viewBookingModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Detail Transaksi</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {currentBooking ? (
                                <div className="container-fluid">
                                    <div className="row mb-4">
                                        <div className="col-12 text-center mb-3">
                                            <span className={`badge fs-6 ${getStatusBadgeColor(currentBooking.status)}`}>
                                                {currentBooking.status}
                                            </span>
                                        </div>
                                        
                                        <div className="col-12">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            <h5 className="fw-bold">#{currentBooking.id} - {currentBooking.nama_service}</h5>
                                                            <p className="text-muted mb-0">{currentBooking.varian_service}</p>
                                                        </div>
                                                        <div className="text-end">
                                                            <h5 className="text-success">{formatPrice(currentBooking.harga_service)}</h5>
                                                            <p className="text-muted mb-0">{currentBooking.tanggal_booking}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="card mb-3">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <Icon icon="mdi:account" className="me-2" />
                                                        Informasi Customer
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <p><strong>Nama:</strong> {getUserName(currentBooking.id_user)}</p>
                                                    <p><strong>Gender:</strong> {currentBooking.gender_select}</p>
                                                    <p><strong>Alamat:</strong> {currentBooking.alamat_user}</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="col-md-6">
                                            <div className="card mb-3">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <Icon icon="mdi:account-tie" className="me-2" />
                                                        Informasi Terapis
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    {currentBooking.id_mitra ? (
                                                        <>
                                                            <p><strong>Nama:</strong> {getMitraName(currentBooking.id_mitra)}</p>
                                                            {mitras[currentBooking.id_mitra] && (
                                                                <>
                                                                    <p><strong>Nomor WA:</strong> {mitras[currentBooking.id_mitra].nomor_wa || '-'}</p>
                                                                    <p><strong>Status Mitra:</strong> {mitras[currentBooking.id_mitra].status || '-'}</p>
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="text-center py-3">
                                                            <Icon icon="mdi:account-off" style={{fontSize: '3rem'}} className="text-muted mb-2" />
                                                            <p className="text-muted mb-3">Belum ada mitra yang ditugaskan</p>
                                                            {currentBooking.status === 'Pending' && (
                                                                <button
                                                                    className="btn btn-warning"
                                                                    onClick={() => {
                                                                        openAssignModal(currentBooking);
                                                                        document.querySelector('#viewBookingModal .btn-close').click();
                                                                    }}
                                                                >
                                                                    <Icon icon="mdi:account-plus" className="me-1" />
                                                                    Tugaskan Mitra
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <Icon icon="mdi:progress-check" className="me-2" />
                                                        Status Progress
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between mb-3">
                                                        <div>
                                                            <strong>Progress Terakhir:</strong>
                                                        </div>
                                                        <div>
                                                            <span className="badge bg-info">{currentBooking.progress_tracking}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Map Modal */}
            <div className="modal fade" id="mapModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Lokasi Customer</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body p-0">
                            {currentBooking && (
                                <div className="ratio ratio-16x9">
                                    <iframe 
                                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentBooking.longitude_user - 0.01},${currentBooking.latitude_user - 0.01},${currentBooking.longitude_user + 0.01},${currentBooking.latitude_user + 0.01}&layer=mapnik&marker=${currentBooking.latitude_user},${currentBooking.longitude_user}`}
                                        style={{border: '0'}}
                                        allowFullScreen=""
                                        aria-hidden="false"
                                        tabIndex="0"
                                    ></iframe>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <p className="me-auto small text-muted">
                                {currentBooking && `${currentBooking.alamat_user}`}
                            </p>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Tutup
                            </button>
                            {currentBooking && (
                                <a 
                                    href={currentBooking.url_maps_user} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn btn-primary"
                                >
                                    <Icon icon="mdi:open-in-new" className="me-1" />
                                    Buka di Google Maps
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Assign Mitra Modal */}
            <div 
                className={`modal fade ${showAssignModal ? 'show' : ''}`} 
                style={{ display: showAssignModal ? 'block' : 'none' }}
                tabIndex="-1" 
                aria-hidden={!showAssignModal}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <AssignMitraModal 
                        booking={bookingToAssign}
                        onSuccess={handleAssignSuccess}
                        onClose={() => {
                            setShowAssignModal(false);
                            setBookingToAssign(null);
                        }}
                    />
                </div>
            </div>
            {showAssignModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default KanbanLayer;